import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Shield, ShieldCheck, Loader2, Download, CalendarDays, Star, Ban, Info, MoreVertical,
  ArrowLeftRight, Send, UserCog, RotateCcw, Check, X, Inbox, Sparkles, ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fmtRange = (s, e) => {
  const d = (x) => new Date(x + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  return s === e ? d(s) : `${d(s)} – ${d(e)}`;
};
const fmtDay = (x) => new Date(x + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" });
const KIND_BADGE = { puente: "Puente", festivo: "Festivo" };

export default function GuardiaRota() {
  const qc = useQueryClient();
  const [year, setYear] = useState(2026);
  const [give, setGive] = useState(null);     // shift a ceder
  const [swap, setSwap] = useState(null);     // shift propio a intercambiar
  const [reassign, setReassign] = useState(null); // shift a reasignar (admin)
  const [pick, setPick] = useState("");       // selección en diálogos

  const q = useQuery({ queryKey: ["guardias-rota", year], queryFn: async () => (await api.get("/guardias/rota", { params: { year } })).data });
  const reqQ = useQuery({ queryKey: ["guardias-requests"], queryFn: async () => (await api.get("/guardias/rota/requests")).data });
  const data = q.data || { shifts: [], totals: {}, members: [], can_manage: false };
  const reqs = reqQ.data || { incoming: [], outgoing: [] };

  const refresh = () => { qc.invalidateQueries({ queryKey: ["guardias-rota"] }); qc.invalidateQueries({ queryKey: ["guardias-requests"] }); };

  const importMut = useMutation({
    mutationFn: async () => (await api.post("/guardias/rota/import-2026")).data,
    onSuccess: (d) => { toast.success(`Calendario ${d.year} importado · ${d.imported} guardias · ${d.libre_absences} libres`); if (d.unresolved?.length) toast.warning(`Sin usuario: ${d.unresolved.join(", ")}`); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo importar"),
  });
  const generateMut = useMutation({
    mutationFn: async () => (await api.post("/guardias/rota/generate", null, { params: { year } })).data,
    onSuccess: (d) => { toast.success(`Calendario ${d.year} generado · ${d.generated} findes · arranca en ${d.start_person} · ${d.libre_absences} libres`); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo generar"),
  });
  const reqMut = useMutation({
    mutationFn: async (body) => (await api.post("/guardias/rota/requests", body)).data,
    onSuccess: () => { toast.success("Solicitud enviada · pendiente de aceptación"); setGive(null); setSwap(null); setPick(""); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo enviar"),
  });
  const actMut = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/guardias/rota/requests/${id}/${action}`)).data,
    onSuccess: (_, v) => { toast.success({ accept: "Guardia actualizada ✓", reject: "Solicitud rechazada", cancel: "Solicitud cancelada" }[v.action]); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const adminMut = useMutation({
    mutationFn: async ({ id, action, to_user_id }) => (await api.patch(`/guardias/rota/shifts/${id}`, { action, to_user_id })).data,
    onSuccess: (d) => { toast.success(`Guardia ${d.detail}`); setReassign(null); setPick(""); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });

  // Agrupa por año-mes (respeta orden cronológico; el calendario 2026 incluye la cola de enero 2027)
  const groups = [];
  const gidx = {};
  for (const s of data.shifts) {
    const key = s.start.slice(0, 7);
    if (gidx[key] === undefined) { gidx[key] = groups.length; groups.push({ key, month: Number(key.slice(5, 7)) - 1, year: Number(key.slice(0, 4)), items: [] }); }
    groups[gidx[key]].items.push(s);
  }
  const totalsSorted = Object.entries(data.totals).sort((a, b) => b[1] - a[1]);
  const swappable = data.shifts.filter((s) => s.swappable);
  const others = (data.members || []).filter((m) => m.user_id !== data.my_user_id);
  const empty = !q.isLoading && data.shifts.length === 0;
  const incPending = reqs.incoming.filter((r) => r.status === "pending");
  const outPending = reqs.outgoing.filter((r) => r.status === "pending");

  // Meses colapsables: por defecto TODO colapsado; el badge "Tu guardia" avisa dónde tienes turno.
  const [monthOverride, setMonthOverride] = useState({});
  const [showPast, setShowPast] = useState(false);
  useEffect(() => { setMonthOverride({}); setShowPast(false); }, [year]);
  const todayStr = new Date().toISOString().slice(0, 10);
  const isPastMonth = (g) => g.items.every((s) => s.end < todayStr);
  const pastCount = groups.filter(isPastMonth).length;
  const visibleGroups = showPast ? groups : groups.filter((g) => !isPastMonth(g));
  const isMonthOpen = (g) => (monthOverride[g.key] !== undefined ? monthOverride[g.key] : false);
  const toggleMonth = (g) => setMonthOverride((o) => ({ ...o, [g.key]: !isMonthOpen(g) }));
  const allOpen = visibleGroups.length > 0 && visibleGroups.every((g) => isMonthOpen(g));
  const setAll = (open) => setMonthOverride(Object.fromEntries(visibleGroups.map((g) => [g.key, open])));

  return (
    <div className="space-y-6" data-testid="guardia-rota">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight"><Shield className="h-5 w-5 text-primary" /> Calendario rotativo de guardias</h2>
          <p className="text-sm text-muted-foreground">Puedes ceder o intercambiar tus guardias con acuerdo.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-9 w-28" data-testid="rota-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[2026, 2027, 2028].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {data.can_manage && year === 2026 && (
            <Button size="sm" onClick={() => importMut.mutate()} disabled={importMut.isPending} data-testid="rota-import-btn">
              {importMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Importar {year}
            </Button>
          )}
          {data.can_manage && year >= 2027 && (
            <Button size="sm" onClick={() => generateMut.mutate()} disabled={generateMut.isPending} data-testid="rota-generate-btn">
              {generateMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}{data.shifts.length ? `Regenerar ${year}` : `Generar ${year}`}
            </Button>
          )}
        </div>
      </div>

      {/* Solicitudes */}
      {(incPending.length > 0 || outPending.length > 0) && (
        <Card data-testid="rota-requests" className="border-primary/30">
          <CardContent className="space-y-2 py-4">
            <p className="flex items-center gap-1.5 text-sm font-semibold"><Inbox className="h-4 w-4 text-primary" /> Solicitudes de guardias</p>
            {incPending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-primary/5 px-3 py-2 text-sm" data-testid={`rota-req-${r.id}`}>
                <span>
                  <b>{r.from_person}</b> {r.type === "swap" ? "te propone intercambiar" : "quiere cederte"} la guardia <b>{r.shift_label}</b>
                  {r.type === "swap" && <> por la tuya <b>{r.target_label}</b></>}
                  {r.note ? <span className="text-muted-foreground"> · "{r.note}"</span> : null}
                </span>
                <span className="flex gap-1.5">
                  <Button size="sm" className="h-7" onClick={() => actMut.mutate({ id: r.id, action: "accept" })} disabled={actMut.isPending} data-testid={`rota-req-accept-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" />Aceptar</Button>
                  <Button size="sm" variant="outline" className="h-7" onClick={() => actMut.mutate({ id: r.id, action: "reject" })} disabled={actMut.isPending} data-testid={`rota-req-reject-${r.id}`}><X className="mr-1 h-3.5 w-3.5" />Rechazar</Button>
                </span>
              </div>
            ))}
            {outPending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground" data-testid={`rota-req-${r.id}`}>
                <span>Enviada a <b>{r.to_person}</b>: {r.type === "swap" ? "intercambio" : "cesión"} de <b>{r.shift_label}</b> · pendiente de aceptación</span>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => actMut.mutate({ id: r.id, action: "cancel" })} disabled={actMut.isPending} data-testid={`rota-req-cancel-${r.id}`}>Cancelar</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {data.can_manage && totalsSorted.length > 0 && (
        <Card data-testid="rota-totals"><CardContent className="flex flex-wrap gap-2 py-4">
          <span className="mr-1 text-xs font-semibold text-muted-foreground">Total {year} (solo Dirección):</span>
          {totalsSorted.map(([person, n]) => (
            <Badge key={person} variant="secondary" className="gap-1 px-2.5 py-1 text-sm" data-testid={`rota-total-${person}`}>{person} <span className="font-bold text-primary">{n}</span></Badge>
          ))}
        </CardContent></Card>
      )}

      {q.isLoading ? (
        <div className="space-y-3"><Skeleton className="h-40 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : empty ? (
        <Card data-testid="rota-empty"><CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="h-6 w-6" /></div>
          <p className="font-semibold">Calendario de guardias {year} sin cargar</p>
          <p className="max-w-md text-sm text-muted-foreground">{!data.can_manage ? "Administración lo cargará en breve." : year === 2026 ? 'Pulsa "Importar 2026" para cargar el calendario cerrado.' : `Pulsa "Generar ${year}" y el sistema creará la rotación automáticamente, continuando el orden desde el final del año anterior.`}</p>
          {data.can_manage && year === 2026 && <Button onClick={() => importMut.mutate()} disabled={importMut.isPending} data-testid="rota-import-empty"><Download className="mr-1.5 h-4 w-4" />Importar calendario 2026</Button>}
          {data.can_manage && year >= 2027 && <Button onClick={() => generateMut.mutate()} disabled={generateMut.isPending} data-testid="rota-generate-empty">{generateMut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}Generar calendario {year}</Button>}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-end gap-2">
            {pastCount > 0 && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => setShowPast((v) => !v)} data-testid="rota-toggle-past">
                {showPast ? `Ocultar meses pasados (${pastCount})` : `Ver meses pasados (${pastCount})`}
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAll(!allOpen)} data-testid="rota-toggle-all">
              {allOpen ? "Colapsar todo" : "Expandir todo"}
            </Button>
          </div>
          {visibleGroups.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="rota-all-past">
              Todas las guardias de {year} ya han pasado.
            </CardContent></Card>
          ) : visibleGroups.map((g) => {
            const open = isMonthOpen(g);
            const mineCount = g.items.filter((s) => s.mine).length;
            return (
            <section key={g.key} data-testid={`rota-month-${g.key}`} className={`overflow-hidden rounded-lg border ${mineCount ? "border-primary/40" : "border-border"}`}>
              <button type="button" onClick={() => toggleMonth(g)} data-testid={`rota-month-toggle-${g.key}`} className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50">
                <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
                <h3 className="font-heading text-sm font-bold uppercase tracking-wide">{MONTHS[g.month]}{g.year !== year ? ` ${g.year}` : ""}</h3>
                <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">{g.items.length}</Badge>
                {mineCount > 0 && (
                  <Badge className="ml-auto gap-1 px-2 py-0.5 text-[11px]" data-testid={`rota-month-mine-${g.key}`}>
                    <Shield className="h-3 w-3" /> Tu guardia{mineCount > 1 ? ` ×${mineCount}` : ""}
                  </Badge>
                )}
              </button>
              {open && (
                <div className="space-y-2 border-t px-3 py-3">
                {g.items.map((s) => (
                  <div key={s.id} data-testid={`rota-shift-${s.id}`} className={`flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-4 py-2.5 ${s.mine ? "border-primary bg-primary/5" : s.annulled ? "border-dashed bg-muted/30" : "bg-card"}`}>
                    <div className="flex min-w-[130px] items-center gap-2"><CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="text-sm font-medium">{fmtRange(s.start, s.end)}</span></div>
                    <div className="flex flex-1 items-center gap-2">
                      {s.annulled ? <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"><Ban className="h-4 w-4" /> Anulada</span>
                        : <span className={`text-sm font-semibold ${s.mine ? "text-primary" : ""}`}>{s.person || "—"}</span>}
                      {s.fixed && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" title="Guardia fija (no movible)" data-testid={`rota-fixed-${s.id}`} />}
                      {s.mine && <Badge className="h-5 px-1.5 text-[10px]">Tú</Badge>}
                      {KIND_BADGE[s.kind] && <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{KIND_BADGE[s.kind]}</Badge>}
                      {s.comments && <span className="text-xs text-muted-foreground">· {s.comments}</span>}
                    </div>
                    {s.grants_libre && s.libre_date && <span className="flex items-center gap-1 rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700" title="Viernes de Libre por Guardia"><Shield className="h-3 w-3" /> Libre {fmtDay(s.libre_date)}</span>}
                    {(s.movable || data.can_manage) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`rota-shift-actions-${s.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {s.movable && <DropdownMenuItem onClick={() => { setSwap(s); setPick(""); }} data-testid={`rota-action-swap-${s.id}`}><ArrowLeftRight className="mr-2 h-4 w-4" />Intercambiar</DropdownMenuItem>}
                          {s.movable && <DropdownMenuItem onClick={() => { setGive(s); setPick(""); }} data-testid={`rota-action-give-${s.id}`}><Send className="mr-2 h-4 w-4" />Ceder a un compañero</DropdownMenuItem>}
                          {data.can_manage && !s.annulled && !s.fixed && <DropdownMenuItem onClick={() => { setReassign(s); setPick(""); }} data-testid={`rota-action-reassign-${s.id}`}><UserCog className="mr-2 h-4 w-4" />Reasignar (Dirección)</DropdownMenuItem>}
                          {data.can_manage && !s.annulled && <DropdownMenuItem className="text-red-600" onClick={() => adminMut.mutate({ id: s.id, action: "annul" })} data-testid={`rota-action-annul-${s.id}`}><Ban className="mr-2 h-4 w-4" />Anular finde</DropdownMenuItem>}
                          {data.can_manage && s.annulled && <DropdownMenuItem onClick={() => adminMut.mutate({ id: s.id, action: "restore" })} data-testid={`rota-action-restore-${s.id}`}><RotateCcw className="mr-2 h-4 w-4" />Restaurar</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> Fija (no movible)</span>
        <span className="flex items-center gap-1"><Shield className="h-3 w-3 text-rose-500" /> Viernes Libre por Guardia</span>
        <span className="flex items-center gap-1"><Ban className="h-3.5 w-3.5" /> Finde anulado por Dirección</span>
        <span className="flex items-center gap-1"><Info className="h-3.5 w-3.5" /> El nº de guardias del mes va solo a la nómina (×120€).</span>
      </div>

      {/* Diálogo Ceder */}
      <Dialog open={!!give} onOpenChange={(o) => !o && setGive(null)}>
        <DialogContent data-testid="rota-give-dialog">
          <DialogHeader><DialogTitle>Ceder guardia {give && fmtRange(give.start, give.end)}</DialogTitle>
            <DialogDescription>El compañero recibirá una solicitud y deberá aceptarla.</DialogDescription></DialogHeader>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="rota-give-select"><SelectValue placeholder="Elige un compañero" /></SelectTrigger>
            <SelectContent>{others.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGive(null)}>Cancelar</Button>
            <Button disabled={!pick || reqMut.isPending} onClick={() => reqMut.mutate({ type: "give", shift_id: give.id, to_user_id: pick })} data-testid="rota-give-submit">Enviar cesión</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Intercambiar */}
      <Dialog open={!!swap} onOpenChange={(o) => !o && setSwap(null)}>
        <DialogContent data-testid="rota-swap-dialog">
          <DialogHeader><DialogTitle>Intercambiar mi guardia {swap && fmtRange(swap.start, swap.end)}</DialogTitle>
            <DialogDescription>Elige la guardia de un compañero. Recibirá una solicitud para aceptar el cambio.</DialogDescription></DialogHeader>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="rota-swap-select"><SelectValue placeholder="Elige la guardia a cambiar" /></SelectTrigger>
            <SelectContent>{swappable.map((t) => <SelectItem key={t.id} value={t.id}>{fmtRange(t.start, t.end)} · {t.person}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSwap(null)}>Cancelar</Button>
            <Button disabled={!pick || reqMut.isPending} onClick={() => reqMut.mutate({ type: "swap", shift_id: swap.id, target_shift_id: pick })} data-testid="rota-swap-submit">Proponer intercambio</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Reasignar (Dirección) */}
      <Dialog open={!!reassign} onOpenChange={(o) => !o && setReassign(null)}>
        <DialogContent data-testid="rota-reassign-dialog">
          <DialogHeader><DialogTitle>Reasignar guardia {reassign && fmtRange(reassign.start, reassign.end)}</DialogTitle>
            <DialogDescription>Dirección: se aplica directamente, sin aceptación del compañero.</DialogDescription></DialogHeader>
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger data-testid="rota-reassign-select"><SelectValue placeholder="Elige a quién asignar" /></SelectTrigger>
            <SelectContent>{(data.members || []).map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassign(null)}>Cancelar</Button>
            <Button disabled={!pick || adminMut.isPending} onClick={() => adminMut.mutate({ id: reassign.id, action: "reassign", to_user_id: pick })} data-testid="rota-reassign-submit">Reasignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
